import { movies as rawMovies } from '@/mocks/movies';
import { newsList as rawNews } from '@/mocks/news';
import type { Movie, NewsArticle } from '@/types/media';

export const movies = rawMovies as unknown as Movie[];
export const newsList = rawNews as unknown as NewsArticle[];

export const PAGE_SIZE = 6;

export const REGIONS = ['全部', '大陆', '欧美', '日韩'];
export const YEARS = ['全部', '2025', '2024', '2023', '2022'];

export const CATEGORY_META: Record<string, { title: string; desc: string; type?: Movie['type'] }> = {
  movie: {
    title: '电影',
    desc: '汇集院线与网络发行的华语及海外电影，涵盖科幻、悬疑、剧情、动作等多种类型。',
    type: 'movie',
  },
  tv: {
    title: '电视剧',
    desc: '精选口碑电视剧集，覆盖古装、都市、刑侦、科幻等热门题材，更新及时。',
    type: 'tv',
  },
  hot: {
    title: '热门推荐',
    desc: '综合热度与口碑，为你挑选当下最值得一看的影视作品。',
  },
};

export const NEWS_CATEGORIES = ['全部', '国内资讯', '国际资讯', '影人报道', '行业观察'];

export function getMovieById(id: string): Movie | undefined {
  return movies.find((item) => item.id === id);
}

export function getNewsById(id: string): NewsArticle | undefined {
  return newsList.find((item) => item.id === id);
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diff <= 0) return '今天';
  if (diff === 1) return '昨天';
  if (diff < 30) return `${diff} 天前`;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function typeLabel(type: Movie['type']): string {
  return type === 'movie' ? '电影' : '电视剧';
}

export interface FilterOptions {
  type?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function filterMovies(options: FilterOptions, source: Movie[] = movies): Movie[] {
  const { type = 'all', region = '全部', year = '全部', sort = 'time' } = options;
  let result = [...source];

  if (type && type !== 'all' && type !== 'hot') {
    result = result.filter((item) => item.type === type);
  }
  if (region && region !== '全部') {
    result = result.filter((item) => item.region === region);
  }
  if (year && year !== '全部') {
    result = result.filter((item) => String(item.year) === year);
  }

  if (sort === 'hot') {
    result.sort((a, b) => b.hot - a.hot);
  } else if (sort === 'rating') {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }

  return result;
}

export function latestMovies(count = 8): Movie[] {
  return [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)).slice(0, count);
}

export function hotMovies(count = 6): Movie[] {
  return [...movies].sort((a, b) => b.hot - a.hot).slice(0, count);
}

export function featuredMovies(count = 5): Movie[] {
  return movies.filter((item) => Boolean(item.banner)).slice(0, count);
}

export function relatedMovies(movie: Movie, count = 4): Movie[] {
  const sameGenre = movies.filter(
    (item) => item.id !== movie.id && item.genres.some((g) => movie.genres.includes(g)),
  );
  const sameType = movies.filter(
    (item) => item.id !== movie.id && item.type === movie.type && !sameGenre.includes(item),
  );
  return [...sameGenre, ...sameType].slice(0, count);
}

export function relatedNews(article: NewsArticle, count = 3): NewsArticle[] {
  const sameCategory = newsList.filter(
    (item) => item.id !== article.id && item.category === article.category,
  );
  const others = newsList.filter(
    (item) => item.id !== article.id && item.category !== article.category,
  );
  return [...sameCategory, ...others].slice(0, count);
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return movies.filter(
    (item) =>
      item.title.toLowerCase().includes(key) ||
      item.subtitle.toLowerCase().includes(key) ||
      item.genres.some((g) => g.toLowerCase().includes(key)) ||
      item.cast.some((c) => c.toLowerCase().includes(key)),
  );
}

export function searchNews(keyword: string): NewsArticle[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return newsList.filter(
    (item) =>
      item.title.toLowerCase().includes(key) ||
      item.summary.toLowerCase().includes(key) ||
      item.category.toLowerCase().includes(key),
  );
}

export function paginate<T>(list: T[], page: number, size = PAGE_SIZE): T[] {
  const start = (page - 1) * size;
  return list.slice(start, start + size);
}